import { DecimalPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { InvoiceAmendmentDetail } from '@app/core/models/invoices/invoice-amendment.model';
import { InvoiceDeductionDetail } from '@app/core/models/invoices/invoice-deduction.model';
import { InvoiceDelegationDetail } from '@app/core/models/invoices/invoice-delegation.model';
import { InvoiceDetailModel } from '@app/core/models/invoices/invoice-detail.model';
import { InvoiceExpenseDetail } from '@app/core/models/invoices/invoice-expense.model';
import { InvoiceParameters } from '@app/core/models/invoices/invoice-parameters.model';
import { InvoiceViewType } from '@app/core/models/invoices/invoice-view-type.model';
import { DeductionTypeRef } from '@app/core/models/referentials/deduction-type.model';
import { InvoicePPRTypeRef } from '@app/core/models/referentials/invoice-ppr-type.model';
import { VatRef } from '@app/core/models/referentials/vat.model';
import * as _ from 'lodash';
import { toNumber } from 'lodash';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class BuildFormGroupsService {
  constructor(
    protected formBuilder: UntypedFormBuilder,
    private decimalPipe: DecimalPipe
  ) {}

  public buildFormControls(
    invoiceDetailModel: InvoiceDetailModel,
    invoiceParams: InvoiceParameters,
    isReadOnlyFormOrWaiting: boolean,
    isReadOnlyForm: boolean,
    canChangeNumber: boolean,
    shouldDisablePrimary: boolean,
    selectedInvoicePPRType: InvoicePPRTypeRef,
    selectedvat: VatRef,
    selectedRecipientEntity: any,
    selectedAsset: any
  ): UntypedFormGroup {
    const requiredForPPRDirectInvoiceBDC =
      !invoiceParams.isInjection &&
      invoiceParams.canCreateInPPR &&
      invoiceDetailModel.IsForPPRDirectInvoiceBDC;
    const requiredForWorkingSituation =
      !invoiceParams.isRejectedInvoice &&
      !invoiceParams.isInjection &&
      invoiceParams.canCreateInPPR &&
      invoiceDetailModel.IsWorkingSituation;

    return this.formBuilder.group({
      IsForPPRDirectInvoiceBDC: new UntypedFormControl(
        {
          disabled:
            isReadOnlyFormOrWaiting ||
            invoiceParams.invoiceViewType == InvoiceViewType.PROMOTIONWORKFLOW,
          value: invoiceDetailModel.IsForPPRDirectInvoiceBDC,
        },
        []
      ),

      IsWorkingSituation: new UntypedFormControl(
        {
          disabled:
            isReadOnlyFormOrWaiting ||
            invoiceParams.invoiceViewType == InvoiceViewType.PROMOTIONWORKFLOW,
          value: invoiceDetailModel.IsWorkingSituation,
        },
        []
      ),

      Number: new UntypedFormControl(
        {
          disabled: invoiceParams.isDuplicateInvoice
            ? false
            : isReadOnlyForm || !canChangeNumber,
          value: invoiceDetailModel.Number,
        },
        [Validators.required]
      ),

      RejectionFlag: new UntypedFormControl(
        {
          disabled: true,
          value: invoiceDetailModel.RejectionFlag,
        },
        !invoiceParams.isInjection ? [Validators.required] : []
      ),

      SupplierSiren: new UntypedFormControl({
        disabled: isReadOnlyFormOrWaiting,
        value: invoiceDetailModel.SupplierSiren,
      }),

      SupplierSiret: new UntypedFormControl({
        disabled: isReadOnlyFormOrWaiting,
        value: invoiceDetailModel.SupplierSiret,
      }),

      SupplierCompanyName: new UntypedFormControl(
        {
          disabled: isReadOnlyForm,
          value: invoiceDetailModel.SupplierCompanyName,
        },
        !invoiceParams.isInjection
          ? [Validators.required, Validators.maxLength(200)]
          : []
      ),

      SupplierRcsNumber: new UntypedFormControl({
        disabled: isReadOnlyFormOrWaiting,
        value: invoiceDetailModel.SupplierRcsNumber,
      }),

      SupplierVatNumber: new UntypedFormControl(
        {
          disabled: isReadOnlyFormOrWaiting,
          value: invoiceDetailModel.SupplierVatNumber,
        },
        !invoiceParams.isInjection && invoiceParams.isVatNumberComplianceEnabled
          ? [Validators.required]
          : []
      ),

      IssuedAt: new UntypedFormControl(
        {
          disabled: isReadOnlyForm,
          value: invoiceDetailModel.IssuedAt
            ? moment(invoiceDetailModel.IssuedAt).format('YYYY-MM-DD')
            : null,
        },
        !invoiceParams.isInjection ? [Validators.required] : []
      ),

      DueDate: new UntypedFormControl(
        {
          disabled: isReadOnlyForm,
          value: invoiceDetailModel.DueDate
            ? moment(invoiceDetailModel.DueDate).format('YYYY-MM-DD')
            : null,
        },
        !invoiceParams.isInjection &&
        !invoiceParams.isManual &&
        invoiceParams.canCreateInPPR
          ? [Validators.required]
          : []
      ),

      EstimatedActDate: new UntypedFormControl(
        {
          disabled: isReadOnlyForm,
          value: invoiceDetailModel.EstimatedActDate
            ? moment(invoiceDetailModel.EstimatedActDate).format('YYYY-MM-DD')
            : null,
        },
        []
      ),

      AmountTaxExcludedWithoutAmendment: new UntypedFormControl(
        {
          disabled: isReadOnlyForm,
          value: this.decimalPipe.transform(
            invoiceDetailModel.AmountTaxExcludedWithoutAmendment,
            '1.2-2'
          ),
        },
        requiredForWorkingSituation ? [] : []
      ),

      AmountTaxExcluded: new UntypedFormControl(
        {
          disabled: isReadOnlyForm,
          value: this.decimalPipe.transform(
            invoiceDetailModel.AmountTaxExcluded,
            '1.2-2'
          ),
        },
        !invoiceParams.isInjection ? [Validators.required] : []
      ),

      AmountTaxIncluded: new UntypedFormControl(
        {
          disabled: isReadOnlyForm,
          value: this.decimalPipe.transform(
            invoiceDetailModel.AmountTaxIncluded,
            '1.2-2'
          ),
        },
        !invoiceParams.isInjection ? [Validators.required] : []
      ),

      Currency: new UntypedFormControl({
        disabled: isReadOnlyFormOrWaiting,
        value: invoiceDetailModel.Currency,
      }),

      IsReverseFactoring: new UntypedFormControl(
        {
          disabled:
            isReadOnlyForm ||
            (!invoiceParams.isControl &&
              !invoiceParams.isDispatchedInvoice &&
              !invoiceParams.isInjection &&
              !invoiceParams.isManual &&
              !invoiceParams.isWaiting),
          value: invoiceDetailModel.IsReverseFactoring,
        },
        []
      ),

      RecipientEntity: new UntypedFormControl(
        {
          disabled:
            (isReadOnlyForm ||
              (!invoiceParams.isRejectedInvoice &&
                !invoiceParams.isPendingForVoucherValidation &&
                !invoiceParams.isInjection &&
                !invoiceParams.isManual)) &&
            !invoiceParams.isDispatchedInvoice,
          value:
            !_.isNil(selectedRecipientEntity) &&
            selectedRecipientEntity.length > 0
              ? [selectedRecipientEntity]
              : [],
        },
        !invoiceParams.isInjection ? [Validators.required] : []
      ),

      PrimaryReference: new UntypedFormControl(
        {
          disabled: shouldDisablePrimary,
          value: [selectedAsset],
        },
        invoiceParams.isPrimaryReferenceRequired ? [Validators.required] : []
      ),

      SecondaryReference: new UntypedFormControl({
        disabled: isReadOnlyForm,
        value: invoiceDetailModel.SecondaryReference,
      }),

      CurrentAssignmentGroupName: new UntypedFormControl({
        disabled: isReadOnlyFormOrWaiting,
        value: invoiceDetailModel.CurrentAssignmentGroupName,
      }),

      VoucherAnalyticalSection: new UntypedFormControl(
        {
          disabled: isReadOnlyFormOrWaiting,
          value: invoiceDetailModel.VoucherAnalyticalSection,
        },
        []
      ),

      VoucherAnalyticalCode: new UntypedFormControl(
        {
          disabled: isReadOnlyFormOrWaiting,
          value: invoiceDetailModel.VoucherAnalyticalCode,
        },
        []
      ),

      VoucherComment: new UntypedFormControl(
        {
          disabled: isReadOnlyFormOrWaiting,
          value: invoiceDetailModel.VoucherComment,
        },
        []
      ),

      VoucherDepartment: new UntypedFormControl({
        disabled: isReadOnlyFormOrWaiting,
        value: invoiceDetailModel.VoucherDepartment,
      }),

      SupplierEvaluaCompanyName: new UntypedFormControl(
        {
          disabled: isReadOnlyForm,
          value: !_.isNil(invoiceDetailModel.SupplierEvaluaCompanyName)
            ? [invoiceDetailModel.SupplierEvaluaCompanyName]
            : [],
        },
        !invoiceParams.isManual &&
        !invoiceParams.isRejectedInvoice &&
        !invoiceParams.isInjection &&
        !invoiceParams.canManageInvoiceAGI &&
        _.isNil(invoiceDetailModel.ClientIdPPR) && _.isNil(invoiceDetailModel.GroupIdPPR)
          ? [Validators.required]
          : []
      ),

      ClientIdPPR: new UntypedFormControl(
        {
          disabled: isReadOnlyForm,
          value: _.isNil(invoiceDetailModel.ClientIdPPR)
            ? null
            : invoiceDetailModel.ClientIdPPR,
        },
        !requiredForPPRDirectInvoiceBDC
          ? []
          : ((_.isNil(invoiceDetailModel.ClientIdPPR) ||
              invoiceDetailModel.ClientIdPPR == '') &&
              _.isNil(invoiceDetailModel.SupplierEvaluaCompanyName)) ||
            !_.isNil(invoiceDetailModel.SupplierEvaluaCompanyName) ||
            !_.isNil(invoiceDetailModel.GroupIdPPR)
          ? []
          : [Validators.required, Validators.maxLength(7)]
      ),

      GroupIdPPR: new UntypedFormControl(
        {
          disabled: isReadOnlyForm,
          value: _.isNil(invoiceDetailModel.GroupIdPPR)
            ? null
            : invoiceDetailModel.GroupIdPPR,
        },
        ((_.isNil(invoiceDetailModel.GroupIdPPR) ||
              invoiceDetailModel.GroupIdPPR == '') &&
              _.isNil(invoiceDetailModel.SupplierEvaluaCompanyName)) ||
            !_.isNil(invoiceDetailModel.SupplierEvaluaCompanyName) ||
            !_.isNil(invoiceDetailModel.ClientIdPPR)
          ? []
          : [Validators.required, Validators.maxLength(7), Validators.pattern(/[AG][A-Z0-9]{5,6}/)]
      ),

      ThirdPartyPayerId: new UntypedFormControl({
        disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
        value: invoiceDetailModel.ThirdPartyPayerId,
      }),

      ThirdPartyPayerName: new UntypedFormControl({
        disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
        value: !_.isNil(invoiceDetailModel.ThirdPartyPayerName)
          ? [invoiceDetailModel.ThirdPartyPayerName]
          : [],
      }),

      SupplierSageErpCode: new UntypedFormControl(
        {
          disabled: true,
          value: invoiceDetailModel.SupplierSageErpCode,
        },
        !invoiceParams.isRejectedInvoice &&
        !invoiceParams.isInjection &&
        !invoiceParams.canManageInvoiceAGI
          ? [Validators.required]
          : []
      ),

      InvoicePPRType: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice
            ? false
            : isReadOnlyFormOrWaiting,
          value: !_.isNil(invoiceDetailModel.InvoicePPRType)
            ? [invoiceDetailModel.InvoicePPRType]
            : [],
        },
        requiredForPPRDirectInvoiceBDC ? [Validators.required] : []
      ),

      InvoicePPRTypeReadOnly: new UntypedFormControl({
        disabled: true,
        value: selectedInvoicePPRType?.Label,
      }),

      InvoicePPRTypeLabel: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice
            ? false
            : isReadOnlyFormOrWaiting,
          value: [selectedInvoicePPRType],
        },
        requiredForPPRDirectInvoiceBDC ? [Validators.required] : []
      ),

      OperationId: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: !_.isNil(invoiceDetailModel.OperationId)
            ? [invoiceDetailModel.OperationId]
            : [],
        },
        !invoiceParams.isRejectedInvoice &&
        !invoiceParams.isInjection &&
        invoiceParams.canCreateInPPR
          ? [Validators.required]
          : []
      ),

      OperationName: new UntypedFormControl(
        {
          disabled: true,
          value: invoiceDetailModel.OperationName,
        },
        !invoiceParams.isInjection && invoiceParams.canCreateInPPR
          ? [Validators.required]
          : []
      ),

      OperationSocieteId: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice
            ? false
            : isReadOnlyFormOrWaiting,
          value: invoiceDetailModel.OperationSocieteId,
        },
        !invoiceParams.isRejectedInvoice &&
        !invoiceParams.isInjection &&
        invoiceParams.canCreateInPPR
          ? [Validators.required]
          : []
      ),

      OperationSocieteName: new UntypedFormControl(
        {
          disabled: true,
          value: invoiceDetailModel.OperationSocieteName,
        },
        !invoiceParams.isInjection && invoiceParams.canCreateInPPR
          ? [Validators.required]
          : []
      ),

      OperationEntiteComptable: new UntypedFormControl(
        {
          disabled: true,
          value: invoiceDetailModel.OperationEntiteComptable,
        },
        !invoiceParams.isInjection && invoiceParams.canCreateInPPR
          ? [Validators.required]
          : []
      ),

      CommandeId: new UntypedFormControl({
        disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
        value: invoiceDetailModel.CommandeId,
      }),

      SelectedCommandeInternalNumber: new UntypedFormControl({
        disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
        value: !_.isNil(invoiceDetailModel.CommandeInternalNumber)
          ? [invoiceDetailModel.CommandeInternalNumber]
          : [],
      }),

      CommandeInternalNumber: new UntypedFormControl({
        disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
        value: invoiceDetailModel.CommandeInternalNumber,
      }),

      MarketId: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice
            ? false
            : isReadOnlyFormOrWaiting,
          value: invoiceDetailModel.MarketId,
        },
        []
      ),

      TradeId: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: invoiceDetailModel.TradeId,
        },
        []
      ),

      TradeLabel: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: !_.isNil(invoiceDetailModel.TradeLabel)
            ? [invoiceDetailModel.TradeLabel]
            : [],
        },
        []
      ),

      SelectedTrade: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: !_.isNil(invoiceDetailModel.TradeId)
            ? [
                invoiceDetailModel.TradeId.concat(
                  ' - ',
                  invoiceDetailModel.TradeLabel
                ),
              ]
            : [],
        },
        []
      ),

      AmountTradeExcludingTax: new UntypedFormControl(
        {
          disabled: true,
          value: this.decimalPipe.transform(
            invoiceDetailModel.AmountTradeExcludingTax,
            '1.2-2'
          ),
        },
        []
      ),

      VatCode: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: !_.isNil(invoiceDetailModel.VatCode)
            ? [invoiceDetailModel.VatCode]
            : [],
        },
        requiredForWorkingSituation ? [Validators.required] : []
      ),

      VatReadOnly: new UntypedFormControl({
        disabled: true,
        value: selectedvat?.Label,
      }),

      SelectedVat: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: !_.isNil(selectedvat?.Label) ? [selectedvat] : [],
        },
        requiredForWorkingSituation ? [Validators.required] : []
      ),

      SituationReference: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value:
            invoiceDetailModel.SituationReference ?? invoiceDetailModel.Number,
        },
        requiredForWorkingSituation ? [Validators.required] : []
      ),

      SituationPeriode: new UntypedFormControl(
        {
          disabled: isReadOnlyForm,
          value: invoiceDetailModel.SituationPeriode
            ? moment(invoiceDetailModel.SituationPeriode).format('YYYY-MM-01')
            : null,
        },
        requiredForWorkingSituation ? [Validators.required] : []
      ),

      CumulationPreviousSituationAmountTaxExcluded: new UntypedFormControl(
        {
          disabled: true,
          value: this.decimalPipe.transform(
            invoiceDetailModel.CumulationPreviousSituationAmountTaxExcluded,
            '1.2-2'
          ),
        },
        []
      ),

      InvoiceExpenses: this.formBuilder.array([]),

      InvoiceAmendments: this.formBuilder.array([]),

      InvoiceDeductions: this.formBuilder.array([]),

      InvoiceDelegations: this.formBuilder.array([]),
    });
  }

  public buildExpenseFormGroup(
    expense: InvoiceExpenseDetail,
    invoiceParams: InvoiceParameters,
    isReadOnlyFormOrWaiting: boolean,
    isReadOnlyForm: boolean,
    selectedvat: VatRef
  ): UntypedFormGroup {
    return this.formBuilder.group({
      ExpenseId: new UntypedFormControl({
        disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
        value: expense.Id,
      }),

      CreatedAt: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: expense.CreatedAt,
        },
        Validators.required
      ),

      FamilyCode: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: !_.isNil(expense.FamilyCode) ? [expense.FamilyCode] : [],
        },
        invoiceParams.isWaiting ? [Validators.required] : []
      ),

      FamilyLabel: new UntypedFormControl(
        {
          disabled: true,
          value: expense.FamilyLabel,
        },
        invoiceParams.isManual || invoiceParams.isWaiting
          ? [Validators.required]
          : []
      ),

      FamilyAccount: new UntypedFormControl(
        {
          disabled: true,
          value: expense.FamilyAccount,
        },
        invoiceParams.isManual || invoiceParams.isWaiting
          ? [Validators.required]
          : []
      ),

      ExpenseLabel: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: expense.Label,
        },
        [Validators.required, Validators.maxLength(255)]
      ),

      ExpenseVat: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: expense.VatCode,
        },
        Validators.required
      ),

      ExpenseVatReadOnly: new UntypedFormControl({
        disabled: invoiceParams.isRejectedInvoice
          ? false
          : isReadOnlyFormOrWaiting,
        value: selectedvat?.Label,
      }),

      ExpenseSelectedVat: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: [selectedvat],
        },
        Validators.required
      ),

      ExpenseAmountTaxExcluded: new UntypedFormControl(
        {
          disabled: isReadOnlyForm,
          value: this.decimalPipe.transform(expense.AmountTaxExcluded, '1.2-2'),
        },
        !invoiceParams.isInjection ? [Validators.required] : []
      ),

      ExpenseAmountVat: new UntypedFormControl(
        {
          disabled: true,
          value: this.decimalPipe.transform(expense.AmountVat, '1.2-2'),
        },
        Validators.required
      ),

      ExpenseAmountTaxIncluded: new UntypedFormControl(
        {
          disabled: true,
          value: this.decimalPipe.transform(expense.AmountTaxIncluded, '1.2-2'),
        },
        !invoiceParams.isInjection ? [Validators.required] : []
      ),
    });
  }

  public buildDeductionFormGroup(
    Deduction: InvoiceDeductionDetail,
    invoiceParams: InvoiceParameters,
    isReadOnlyFormOrWaiting: boolean,
    isReadOnlyForm: boolean,
    selectedDeductionType: DeductionTypeRef
  ): UntypedFormGroup {
    return this.formBuilder.group({
      DeductionId: new UntypedFormControl({
        disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
        value: Deduction.Id,
      }),

      CreatedAt: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: Deduction.CreatedAt,
        },
        Validators.required
      ),

      DeductionTypeCode: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice
            ? false
            : isReadOnlyFormOrWaiting,
          value: Deduction.Code,
        },
        Validators.required
      ),

      DeductionTypeLabel: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice
            ? false
            : isReadOnlyFormOrWaiting,
          value: [selectedDeductionType],
        },
        Validators.required
      ),

      DeductionTypeLabelReadOnly: new UntypedFormControl(
        {
          disabled: true,
          value: selectedDeductionType?.LabelWithcode,
        },
        []
      ),

      SelectedDeductionType: new UntypedFormControl({
        disabled: invoiceParams.isRejectedInvoice
          ? false
          : isReadOnlyFormOrWaiting,
        value: [selectedDeductionType],
      }),

      DeductionPercentage: new UntypedFormControl({
        disabled: true,
        value: '',
      }),

      AmountProvisionedTaxExcluded: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice
            ? selectedDeductionType.IsProvisionedActive
              ? false
              : true
            : isReadOnlyFormOrWaiting,
          value: this.decimalPipe.transform(
            Deduction.AmountProvisionedTaxExcluded,
            '1.2-2'
          ),
        },
        Validators.required
      ),

      AmountReleasedTaxExcluded: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice
            ? selectedDeductionType.IsReleasedActive
              ? false
              : true
            : isReadOnlyFormOrWaiting,
          value: this.decimalPipe.transform(
            Deduction.AmountReleasedTaxExcluded,
            '1.2-2'
          ),
        },
        Validators.required
      ),

      AmountRetainedTaxExcluded: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice
            ? selectedDeductionType.IsRetainedActive
              ? false
              : true
            : isReadOnlyFormOrWaiting,
          value: this.decimalPipe.transform(
            Deduction.AmountRetainedTaxExcluded,
            '1.2-2'
          ),
        },
        Validators.required
      ),
    });
  }

  public buildAmendmentFormGroup(
    amendment: InvoiceAmendmentDetail,
    invoiceParams: InvoiceParameters,
    isReadOnlyForm: boolean
  ): UntypedFormGroup {
    return this.formBuilder.group({
      AmendmentId: new UntypedFormControl({
        disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
        value: amendment.Id,
      }),

      CreatedAt: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: amendment.CreatedAt,
        },
        Validators.required
      ),

      AmendmentCode: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: amendment.Code,
        },
        []
      ),

      AmendmentLabel: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value:
            !_.isNil(amendment.Label) && !_.isNil(amendment.Code)
              ? [
                  {
                    numeroAvenant: toNumber(amendment.Code),
                    label: amendment.Label,
                  },
                ]
              : [],
        },
        []
      ),

      AmendmentLabelReadOnly: new UntypedFormControl(
        {
          disabled: true,
          value: !_.isNil(amendment.Label) ? amendment.Label : '',
        },
        []
      ),

      AmendmentOriginalAmountTaxExcluded: new UntypedFormControl(
        {
          disabled: true,
          value: this.decimalPipe.transform(0, '1.2-2'),
        },
        []
      ),

      AmendmentOriginalCumulTaxExcluded: new UntypedFormControl(
        {
          disabled: true,
          value: this.decimalPipe.transform(0, '1.2-2'),
        },
        []
      ),

      AmendmentDescription: new UntypedFormControl(
        {
          disabled: true,
          value: '',
        },
        []
      ),

      AmendmentAmountTaxExcluded: new UntypedFormControl(
        {
          disabled: isReadOnlyForm,
          value: this.decimalPipe.transform(
            amendment.AmountTaxExcluded,
            '1.2-2'
          ),
        },
        !invoiceParams.isInjection ? [Validators.required] : []
      ),

      AmendmentAmountVat: new UntypedFormControl(
        {
          disabled: true,
          value: this.decimalPipe.transform(amendment.AmountVat, '1.2-2'),
        },
        Validators.required
      ),

      AmendmentAmountTaxIncluded: new UntypedFormControl(
        {
          disabled: true,
          value: this.decimalPipe.transform(
            amendment.AmountTaxIncluded,
            '1.2-2'
          ),
        },
        !invoiceParams.isInjection ? [Validators.required] : []
      ),
    });
  }

  public buildDelegationFormGroup(
    delegation: InvoiceDelegationDetail,
    invoiceParams: InvoiceParameters,
    isReadOnlyForm: boolean
  ): UntypedFormGroup {
    return this.formBuilder.group({
      DelegateInternalId: new UntypedFormControl({
        disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
        value: delegation.Id,
      }),

      CreatedAt: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: delegation.CreatedAt,
        },
        Validators.required
      ),

      DelegateId: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: delegation.DelegateId,
        },
        Validators.required
      ),

      DelegateName: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: [delegation.DelegateName],
        },
        Validators.required
      ),

      CreditorId: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: delegation.CreditorId,
        },
        []
      ),

      CreditorName: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: [delegation.CreditorName],
        },
        []
      ),

      DelegateAmountRemainsTaxIncluded: new UntypedFormControl(
        {
          disabled: invoiceParams.isRejectedInvoice ? false : isReadOnlyForm,
          value: this.decimalPipe.transform(
            delegation.AmountRemainsTaxIncluded,
            '1.2-2'
          ),
        },
        Validators.required
      ),

      DelegateAmountPackageTaxIncluded: new UntypedFormControl(
        {
          disabled: true,
          value: this.decimalPipe.transform(
            delegation.AmountPackageTaxIncluded,
            '1.2-2'
          ),
        },
        []
      ),
    });
  }

  public setRequiredDirectInvoice(
    invoiceDetailModel: InvoiceDetailModel,
    invoiceDetailForm: UntypedFormGroup,
    requiredvalue: boolean
  ) {
    invoiceDetailForm.controls.CommandeId.setValidators([]);
    invoiceDetailForm.controls.CommandeInternalNumber.setValidators([]);
    invoiceDetailForm.controls.InvoicePPRType.setValidators(
      requiredvalue ? [Validators.required] : []
    );
    invoiceDetailForm.controls.InvoicePPRTypeLabel.setValidators(
      requiredvalue ? [Validators.required] : []
    );

    if (!requiredvalue) {
      invoiceDetailModel.CommandeId = null;
      invoiceDetailModel.CommandeInternalNumber = null;
      invoiceDetailModel.InvoicePPRType = null;

      invoiceDetailForm.controls.CommandeId.patchValue(
        invoiceDetailModel.CommandeId
      );
      invoiceDetailForm.controls.CommandeInternalNumber.patchValue(
        invoiceDetailModel.CommandeInternalNumber
      );
      invoiceDetailForm.controls.InvoicePPRType.patchValue(
        invoiceDetailModel.InvoicePPRType
      );
      invoiceDetailForm.controls.InvoicePPRTypeLabel.patchValue([]);
    }
  }

  public setRequiredWorkingSituation(
    invoiceDetailModel: InvoiceDetailModel,
    invoiceDetailForm: UntypedFormGroup,
    requiredvalue: boolean
  ) {
    invoiceDetailForm.controls.ClientIdPPR.setValidators([]);
    invoiceDetailForm.controls.SituationPeriode.setValidators(
      requiredvalue ? [Validators.required] : []
    );
    invoiceDetailForm.controls.SituationReference.setValidators(
      requiredvalue ? [Validators.required] : []
    );
    invoiceDetailForm.controls.VatCode.setValidators(
      requiredvalue ? [Validators.required] : []
    );
    invoiceDetailForm.controls.SelectedVat.setValidators(
      requiredvalue ? [Validators.required] : []
    );

    if (!requiredvalue) {
      invoiceDetailModel.SituationPeriode = null;
      invoiceDetailModel.VatCode = null;

      invoiceDetailForm.controls.SituationPeriode.patchValue(
        invoiceDetailModel.SituationPeriode
      );
      invoiceDetailForm.controls.VatCode.patchValue(invoiceDetailModel.VatCode);
    }

    invoiceDetailModel.SituationReference = invoiceDetailModel.Number;
    invoiceDetailForm.controls.SituationReference.patchValue(
      invoiceDetailModel.SituationReference
    );
  }

  public markFormGroupTouched(formGroup: UntypedFormGroup) {
    (<any>Object).values(formGroup.controls).forEach((control) => {
      control.markAsTouched();

      if (control.controls) {
        this.markFormGroupTouched(control);
      }
    });
  }
}
